import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useDataStore } from '../stores/dataStore';
import { useUIStore } from '../stores/uiStore';
import { parseFile, validateFile } from '../utils/dataParser';
import { SampleDatasets } from './SampleDatasets';
import { analytics } from '../utils/analytics';

export function DataUpload() {
  const setRawData = useDataStore(state => state.setRawData);
  const setLoading = useUIStore(state => state.setLoading);
  const setError = useUIStore(state => state.setError);
  const clearError = useUIStore(state => state.clearError);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];
      clearError();

      // Validate file
      const validation = validateFile(file);
      if (!validation.valid) {
        setError({
          hasError: true,
          message: 'Invalid file',
          details: validation.error,
        });
        return;
      }

      // Parse file
      try {
        setLoading({
          isLoading: true,
          message: 'Parsing file...',
          progress: 0,
        });

        const parsedData = await parseFile(file);

        setRawData(parsedData);
        setLoading({ isLoading: false });

        // Track file upload
        const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'unknown';
        analytics.track('file_uploaded', {
          file_type: fileExtension,
          rows: parsedData.rows.length,
          columns: parsedData.columns.length,
          size_mb: (file.size / (1024 * 1024)).toFixed(2),
        });
      } catch (error) {
        setLoading({ isLoading: false });
        setError({
          hasError: true,
          message: 'Failed to parse file',
          details: error instanceof Error ? error.message : String(error),
        });
      }
    },
    [setRawData, setLoading, setError, clearError]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    multiple: false,
    maxSize: 50 * 1024 * 1024, // 50MB
  });

  return (
    <div className="w-full max-w-2xl mx-auto p-6">
      <div
        {...getRootProps()}
        className={`
          group border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer
          shadow-sm hover:shadow-md transition-all duration-300 transform
          ${
            isDragActive
              ? 'border-blue-500 bg-blue-50/30 shadow-inner scale-[0.99]'
              : 'border-gray-300 bg-white hover:border-blue-500/80 hover:bg-slate-50/30'
          }
        `}
      >
        <input {...getInputProps()} />

        <div className="space-y-4">
          <div className="mx-auto w-14 h-14 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center border border-blue-100/50 group-hover:scale-105 transition-transform duration-300">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
              />
            </svg>
          </div>

          {isDragActive ? (
            <p className="text-base font-semibold text-blue-600 animate-pulse">Drop your file here...</p>
          ) : (
            <>
              <div className="space-y-1">
                <p className="text-base font-bold text-gray-800">
                  Drop your CSV file here, or <span className="text-blue-600 group-hover:text-blue-700 transition-colors">browse</span>
                </p>
                <p className="text-xs text-gray-400">
                  Supports file uploads up to 50MB
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="mt-5 bg-slate-50 border border-gray-200/55 rounded-xl p-4 text-xs text-gray-550 leading-relaxed space-y-2">
        <p className="font-bold text-gray-800 flex items-center space-x-1.5">
          <span>📋</span>
          <span>Requirements & Security</span>
        </p>
        <ul className="list-disc list-inside space-y-1 pl-1 text-[11px] text-gray-500">
          <li>File must contain a date/time column and a numeric target column.</li>
          <li>Minimum 10 data points are required to train forecasting models.</li>
          <li><strong>Privacy First:</strong> Your dataset remains in your browser. All computations are fully client-side.</li>
        </ul>
      </div>

      <SampleDatasets
        onLoadSample={(file) => {
          onDrop([file]);
        }}
      />
    </div>
  );
}
